/**
 * Firestore Error Boundary
 * 
 * Catches Firestore SDK internal assertion errors during component unmount.
 * This is a workaround for Firebase Firestore SDK 12.0.0 listener cleanup bugs.
 * 
 * The error occurs when Firestore listeners are cleaned up during React's
 * commitHookEffectListUnmount phase, causing the app to crash with:
 * "FIRESTORE (12.0.0) INTERNAL ASSERTION FAILED: Unexpected state"
 * 
 * GUARDRAIL: Only catch the known internal assertion signature
 * - Pattern: "FIRESTORE" + "INTERNAL ASSERTION"
 * - This specifically targets SDK-level watch stream state bugs during unmount
 * - Real Firestore permission/network errors (permission-denied, unavailable, etc.) are NOT caught
 * - Other Firebase errors are NOT caught
 * - Other errors propagate to parent boundaries
 */

import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: any;
}

export class FirestoreErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: any): State {
    // Check if this is a Firestore internal assertion error
    const isFirestoreError = 
      error?.message?.includes('FIRESTORE') &&
      error?.message?.includes('INTERNAL ASSERTION');
    
    if (isFirestoreError) {
      console.warn('[Firestore Error Boundary] Caught Firestore SDK assertion error:', error);
      return { hasError: true, error };
    }
    
    // Let other errors propagate to parent boundaries
    return { hasError: false };
  }

  componentDidCatch(error: any, info: any) {
    if (error?.message?.includes('FIRESTORE') && error?.message?.includes('INTERNAL ASSERTION')) {
      console.warn('[Firestore Error Boundary] Component stack:', info.componentStack);
      // Optionally log to analytics or monitoring service
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white flex items-center justify-center">
          <div className="text-center p-6">
            <h3 className="text-lg font-semibold text-[#00d4aa] mb-2">Firestore Temporarily Unavailable</h3>
            <p className="text-gray-300 mb-4">A Firestore SDK error occurred during cleanup. This is a known issue with the current SDK version.</p>
            <button
              onClick={() => this.setState({ hasError: false })}
              className="px-4 py-2 bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
